import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookings } from '@/hooks/useBookings';

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function BookingsTab() {
  const { bookings, loading, error, refresh } = useBookings();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">{t('profile.loadingBookings')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">{t('profile.somethingWrong')}</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
        <button
          onClick={refresh}
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          {t('profile.tryAgain')}
        </button>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
          <i className="ri-suitcase-line text-2xl text-secondary-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">{t('profile.noBookings')}</h3>
        <p className="mt-1 text-sm text-secondary-500">
          {t('profile.noBookingsText')}
        </p>
        <Link
          to="/tours"
          className="mt-5 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap"
        >
          {t('profile.exploreTours')}
          <i className="ri-arrow-right-line text-sm" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookings.map((b) => {
        const guests = (Number(b.adults) || 0) + (Number(b.children) || 0);
        const total = Number(b.total_price) || 0;

        return (
          <div
            key={b.id}
            className="bg-white border border-secondary-200 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-4"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                {b.tour_id ? (
                  <Link
                    to={`/tour/${b.tour_id}`}
                    className="group font-heading text-base font-bold text-secondary-900 leading-snug hover:text-primary-600 transition-colors inline-flex items-start gap-1.5"
                  >
                    <span>{b.tour_title}</span>
                    <i className="ri-external-link-line text-sm text-primary-500 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                ) : (
                  <h3 className="font-heading text-base font-bold text-secondary-900 leading-snug">
                    {b.tour_title}
                  </h3>
                )}
              </div>
              {b.booking_code && (
                <div className="mt-3 inline-flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-md px-3 py-1.5">
                  <span className="text-xs font-medium text-primary-600">{t('profile.bookingCode')}</span>
                  <span className="font-mono text-sm font-bold tracking-widest text-primary-700">
                    {b.booking_code}
                  </span>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-500">
                {b.destination && (
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-map-pin-line text-base text-primary-600" />
                    {b.destination}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line text-base text-primary-600" />
                  {formatDate(b.travel_date)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-group-line text-base text-primary-600" />
                  {guests} {guests > 1 ? t('profile.guests') : t('profile.guest')}
                </span>
              </div>
            </div>
            <div className="md:text-right shrink-0">
              <p className="text-sm text-secondary-500">{t('profile.total')}</p>
              <p className="font-heading text-xl font-bold text-secondary-900">
                ${total.toLocaleString()}
              </p>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-secondary-400">
                <i className="ri-time-line text-xs" />
                {formatDate(b.created_at?.split('T')[0] || '')}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}